import React from 'react';
import { motion } from 'framer-motion';
import { THEME } from '../../utils/theme';

const MobileMenu = ({ isOpen, onClose, user, onSync, onSettings, onLogout, libraryMode, setLibraryMode, syncing, onDiscover, onUserDiscovery, onAddGame }) => {
  if (!isOpen) return null;
  const myColor = user?.theme_color || THEME.success;

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{ position: 'fixed', inset: 0, zIndex: 999, background: 'rgba(5,5,10,0.5)' }}
        onClick={onClose}
      />
      <motion.div
        initial={{ opacity: 0, y: -20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -20, scale: 0.95 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        style={{
          position: 'fixed',
          top: '64px',
          left: '12px',
          right: '12px',
          background: THEME.glass,
          backdropFilter: 'blur(32px) saturate(180%)',
          WebkitBackdropFilter: 'blur(32px) saturate(180%)',
          border: `1px solid ${THEME.glassBorder}`,
          borderRadius: '20px',
          padding: '20px',
          zIndex: 1000,
          boxShadow: THEME.modalShadow
        }}
      >
        {/* User Info */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', paddingBottom: '16px', borderBottom: `1px solid ${THEME.border}` }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: `linear-gradient(135deg, ${THEME.omega}, ${THEME.supe})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', color: '#000' }}>
            {user?.display_name?.charAt(0) || '?'}
          </div>
          <div>
            <div style={{ color: THEME.text, fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
              {user?.display_name}
              {user?.is_founder && <span title="Founder" style={{ fontSize: '14px' }}>{user?.founder_icon || '⭐'}</span>}
            </div>
            <div style={{ color: THEME.textMuted, fontSize: '12px' }}>@{user?.username}</div>
          </div>
        </div>

        {/* Library Toggle */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '11px', color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>Library</div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {[
              { mode: 'coop', label: 'Co-op Library' },
              { mode: user?.username, label: 'My Library' }
            ].map(({ mode, label }) => (
              <button key={mode} onClick={() => { setLibraryMode(mode); onClose(); }} className="press-effect" style={{
                flex: 1,
                background: libraryMode === mode ? (mode === 'coop' ? `linear-gradient(135deg, ${THEME.omega}, ${THEME.supe})` : myColor) : THEME.bg,
                color: libraryMode === mode ? '#000' : THEME.textMuted,
                border: `1px solid ${libraryMode === mode ? 'transparent' : THEME.border}`,
                borderRadius: '10px',
                padding: '12px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer'
              }}>{label}</button>
            ))}
          </div>
        </div>

        {/* Discover Section */}
        {onDiscover && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '11px', color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>Discover</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              {/* Smart Suggest - Featured */}
              <button onClick={() => { onDiscover('smartSuggest'); onClose(); }} className="press-effect" style={{
                gridColumn: '1 / -1',
                background: `linear-gradient(90deg, ${THEME.omega}15, ${THEME.supe}15)`,
                border: `1px solid ${THEME.success}40`,
                color: THEME.text,
                borderRadius: '10px',
                padding: '12px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ fontSize: '18px' }}>🎯</span> What Should We Play?
              </button>
              {[
                { key: 'lastTen', icon: '🕹️', label: 'Last 10 Played' },
                { key: 'topTen', icon: '🏆', label: 'Top 10 (30 Days)' },
                { key: 'recentlyAdded', icon: '✨', label: 'Recently Added' },
                { key: 'hiddenGems', icon: '💎', label: 'Hidden Gems' },
                { key: 'rateUnrated', icon: '⭐', label: 'Rate Unrated' },
                { key: 'ratingComparison', icon: '📊', label: 'Ratings Compare' },
                { key: 'genreBreakdown', icon: '🎯', label: 'Genre Breakdown' },
                { key: 'yearReview', icon: '📅', label: 'Year in Review' },
                { key: 'backlogBurndown', icon: '📉', label: 'Backlog Burndown' },
                { key: 'queue', icon: '📋', label: 'Next Up Queue' },
              ].map(item => (
                <button key={item.key} onClick={() => { onDiscover(item.key); onClose(); }} className="press-effect" style={{
                  background: THEME.bg,
                  color: THEME.text,
                  border: `1px solid ${THEME.border}`,
                  borderRadius: '10px',
                  padding: '10px',
                  fontSize: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <span>{item.icon}</span> {item.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Social */}
        {onUserDiscovery && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '11px', color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>Social</div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => { onUserDiscovery(); onClose(); }} className="press-effect" style={{
                flex: 1,
                background: `linear-gradient(90deg, ${THEME.info}15, ${THEME.supe}15)`,
                border: `1px solid ${THEME.info}40`,
                color: THEME.text,
                borderRadius: '10px',
                padding: '12px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ fontSize: '18px' }}>👥</span> Find Users
              </button>
              <a href="https://600mmr.com" target="_blank" rel="noopener noreferrer" onClick={onClose} className="press-effect" style={{
                background: `${THEME.accent}15`,
                border: `1px solid ${THEME.accent}40`,
                color: THEME.accent,
                borderRadius: '10px',
                padding: '12px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                textDecoration: 'none'
              }}>
                <span style={{ fontSize: '18px' }}>🚀</span> 600mmr
              </a>
              <a href="/redline/" onClick={onClose} className="press-effect" style={{
                background: 'rgba(255,34,34,0.08)',
                border: '1px solid rgba(255,34,34,0.25)',
                color: '#ff4444',
                borderRadius: '10px',
                padding: '12px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                textDecoration: 'none'
              }}>
                <span style={{ fontSize: '18px' }}>🏎️</span> RedLine FC
              </a>
            </div>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <button onClick={() => { onSync(); onClose(); }} disabled={syncing} className="press-effect" style={{
            background: `linear-gradient(135deg, ${THEME.omega}, ${THEME.supe})`,
            color: '#000',
            border: 'none',
            borderRadius: '10px',
            padding: '14px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: syncing ? 'not-allowed' : 'pointer',
            opacity: syncing ? 0.7 : 1
          }}>
            {syncing ? '⟳ Syncing...' : '⟳ Sync All'}
          </button>
          {onAddGame && (
            <button onClick={() => { onAddGame(); onClose(); }} className="press-effect" style={{
              background: `${THEME.accent}15`,
              color: THEME.accent,
              border: `1px solid ${THEME.accent}40`,
              borderRadius: '10px',
              padding: '14px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}>
              + Add Non-Steam Game
            </button>
          )}
          <button onClick={() => { onSettings(); onClose(); }} className="press-effect" style={{
            background: THEME.bg,
            color: THEME.text,
            border: `1px solid ${THEME.border}`,
            borderRadius: '10px',
            padding: '14px',
            fontSize: '14px',
            cursor: 'pointer'
          }}>
            ⚙️ Settings
          </button>
          <button onClick={() => { onLogout(); onClose(); }} className="press-effect" style={{
            background: 'transparent',
            color: THEME.danger,
            border: 'none',
            borderRadius: '10px',
            padding: '14px',
            fontSize: '14px',
            cursor: 'pointer'
          }}>
            Logout
          </button>
        </div>
      </motion.div>
    </>
  );
};

export default MobileMenu;
